import type { DocsItem } from "@academy/components"

import { getPrimaryKidpreneursAcademyLink } from "utils/getKidpreneursAcademyLink"
import { getPrimaryKidsCraftsLink } from "utils/getKidsCraftsLink"

export const AGREEMENT_CLIENT_STATIC: DocsItem[] = [
  {
    title: "1. Acceptance of Terms",
    content: [
      {
        html: `By accessing or using <a class="underline text-primary" target="_blank" href="https://kidpreneurs.academy">https://Kidpreneurs.academy</a> and/or <a class="underline text-primary" target="_blank" href="https://kidscrafts.shop">https://KidsCrafts.shop</a> (the
“Sites”), you agree to comply with and be bound by this User Agreement and Terms of
Use, as well as our Privacy Policy. If you do not agree with these Terms, please do not
use our Sites.`,
      },
      {
        html: `For ${getPrimaryKidpreneursAcademyLink()}, use of the platform is intended for parents or legal
guardians who register children for educational programs.`,
      },
      {
        html: `For ${getPrimaryKidsCraftsLink()}, parental or legal guardian consent is required for any child user
participation or use of an account.`,
      },
    ],
  },
  {
    title: "2. Eligibility",
    content: [
      {
        html: `<ul class="list-inside list-disc mb-[1rem]">
                    <li class="docs__item mb-[1rem]"><strong>Kidpreneurs.academy:</strong> Users must be parents or legal guardians aged 19 or older, or of legal age in their region.</li>
                    <li class="docs__item"><span><strong>KidsCrafts.shop:</strong> This is a specially developed child-focused an e-commerce training platform designed as an educational marketplace where children can learn and practice business and sales skills <strong>under the supervision and consent of a parent or legal guardian.</strong></span>
                      <ul class="list-inside list-disc mb-[1rem] ml-[1rem] mt-[0.5rem]">
                        <li class="docs__item">Parents or legal guardians are responsible for creating, managing, and monitoring their accounts and their child’s activity on the site.</li>
                        <li class="docs__item">By creating an account, you confirm that you are the parent or legal guardian of the child and that you consent to their participation.</li>
                      </ul>
                    </li>
                  </ul>
            `,
      },
    ],
  },
  {
    title: "3. Account Responsibilities",
    content: [
      {
        list: {
          description: "",
          items: [
            "You agree to provide accurate and current information during registration and to keep your account details secure.",
            "Our payment processor, Stripe, will verify your identity to ensure you are of legal age and that all provided information is accurate.",
            "Payments will be processed and accepted through our payment processor, Stripe.",
            "You are responsible for all activity conducted under your account.",
            "You must notify us immediately of any unauthorized use or suspected breach of security.",
            "Parents/guardians must ensure that any content or listings created by their children on KidsCrafts.shop comply with these Terms and have been reviewed by them.",
          ],
        },
      },
    ],
  },
  {
    title: "4. Educational Marketplace Conduct (KidsCrafts.shop)",
    content: [
      {
        html: `${getPrimaryKidsCraftsLink()} is a <strong>learning-based, child-friendly marketplace</strong> designed for educational purposes.`,
      },
      {
        list: {
          description: "By using the site, you agree that:",
          items: [
            "Children may only create listings and engage in simulated or supervised transactions with full parental or legal guardian oversight.",
            "Product listings must be appropriate for all audiences and may not include any prohibited items, personal identifying information, or child-related information.",
            "Parents or legal guardians are legally responsible for their children's activities and must review and approve each listing for compliance with COPPA, PIPEDA, and other applicable laws prior to submission.",
            "All listings are subject to review and moderation by KidsCrafts.shop staff or designated partners for safety, accuracy, and appropriateness.",
            "The platform may remove or suspend listings or accounts that violate these Terms or pose a safety risk.",
          ],
        },
      },
    ],
  },
  {
    title: "5. Payments and Transactions",
    content: [
      {
        list: {
          description: "",
          items: [
            "All payments for classes, subscriptions, or services on Kidpreneurs.academy and KidsCrafts.shop are processed securely through approved third-party payment providers such as Stripe.",
            "You agree to provide accurate payment information and authorize us to charge applicable fees.",
            "Fees are non-refundable unless otherwise stated.",
            "Parents are responsible for all payments.",
          ],
        },
      },
    ],
  },
  {
    title: "6. Intellectual Property",
    content: [
      {
        html: `All content, logos, text, designs, graphics, and software on ${getPrimaryKidpreneursAcademyLink()} and ${getPrimaryKidsCraftsLink()} are owned or licensed by the Academy and protected under applicable copyright, trademark, and intellectual property laws.`,
      },
      {
        list: {
          description: "You may not:",
          items: [
            "Copy, reproduce, or distribute content from our Sites without written permission.",
            "Use our trademarks or branding without authorization.",
          ],
        },
      },
    ],
  },
  {
    title: "7. Prohibited Activities",
    content: [
      {
        list: {
          description: "You agree not to:",
          items: [
            "Use the Sites for any unlawful, harmful, or fraudulent purpose.",
            "Submit or post any content that is offensive, inappropriate, or violates the rights of others.",
            "Attempt to bypass moderation, access another user’s account, or interfere with the platform’s operations.",
          ],
        },
      },
      "Violations may result in suspension or termination of your account.",
    ],
  },
  {
    title: "8. Safety, Monitoring, and Moderation",
    content: [
      {
        list: {
          description: "To maintain a secure environment, we may:",
          items: [
            "Monitor and review user activity and content on KidsCrafts.shop.",
            "Remove content or suspend access if safety or policy violations occur.",
            "Report illegal or harmful behavior to appropriate authorities.",
          ],
        },
      },
      "Moderation is intended to ensure that all users - especially children - learn safely.",
    ],
  },
  {
    title: "9. Termination of Use",
    content: [
      "We reserve the right to suspend or terminate your access to our Sites at any time, without notice, if we believe you have violated these Terms or engaged in unsafe or inappropriate conduct.",
      {
        list: {
          description: "Upon termination:",
          items: [
            "Your access to the Sites will be revoked.",
            "User-generated content may be deleted, except as required by law.",
          ],
        },
      },
      {
        html: 'Parents may request account deletion through our <a class="underline text-primary" target="_blank" href="/contacts">Contact us</a> form.',
      },
    ],
  },
  {
    title: "10. Disclaimers",
    content: [
      {
        list: {
          description: "",
          items: [
            "The Sites are provided for educational and training purposes.",
            "We make no guarantees that the Sites will always operate uninterrupted or error-free.",
            "We are not liable for any loss, damage, or misunderstanding arising from user-generated content or external links.",
          ],
        },
      },
    ],
  },
  {
    title: "11. Limitation of Liability",
    content: [
      {
        html: `<ul class="list-inside list-disc mb-[1rem]">
                    <span class="docs__item">To the fullest extent permitted by law:</span>
                    <li class="docs__item mt-[0.5rem]">
                    The Academy, its affiliates, and partners shall not be liable for any indirect incidental, or consequential damages resulting from your use of the Sites.
                    </li>
                    <li class="docs__item">
                    Parents or legal guardians are fully responsible for supervising their child’s activity on ${getPrimaryKidsCraftsLink()}.
                    </li>
                  </ul>
            `,
      },
    ],
  },
  {
    title: "12. Indemnification",
    content: [
      {
        list: {
          description: "",
          items: [],
        },
      },
      {
        html: `<ul class="list-inside list-disc mb-[1rem]">
                    <span class="docs__item">You agree to indemnify and hold harmless ${getPrimaryKidpreneursAcademyLink()}, ${getPrimaryKidsCraftsLink()}, and their affiliates, officers, and staff from any claims, damages, or expenses arising from:</span>
                    <li class="docs__item mt-[0.5rem]">
                    Your or your child’s use of the Sites.
                    </li>
                    <li class="docs__item">
                    Any violation of these Terms.
                    </li>
                    <li class="docs__item">
                    Any content you submit, post, or share.
                    </li>
                  </ul>
            `,
      },
    ],
  },
  {
    title: "13. Changes to These Terms",
    content: [
      "We may update or modify these Terms at any time. Updates will be posted on the Sites. Continued use of our Sites after changes are made constitutes your acceptance of the updated Terms.",
    ],
  },
  {
    title: "14. Governing Law",
    content: [
      "These Terms are governed by the laws of Canada and the province where the Academy is headquartered, without regard to conflict of law principles.",
    ],
  },
  {
    title: "15. Contact Us",
    content: [
      {
        html: 'If you have any questions about these Terms of Use, please contact us via the <a class="underline text-primary" target="_blank" href="/contacts">Contact us</a> form.',
      },
    ],
  },
]
